<template>
  <div class="message-input-container" ref="inputContainer">
    <div v-if="replyingTo" class="reply-preview">
      <div class="reply-content">
        <div class="reply-header">
          <i class="bi bi-reply"></i>
          <span>Respondendo a {{ replyingTo.user_name || 'mensagem' }}</span>
        </div>
        <div class="reply-text">{{ replyingTo.content || '[Mídia]' }}</div>
      </div>
      <button @click="$emit('cancel-reply')" class="cancel-reply-btn">
        <i class="bi bi-x"></i>
      </button>
    </div>

    <div v-if="previewFiles.length > 0" class="preview-files">
      <div v-for="(file, index) in previewFiles" :key="index" class="preview-item">
        <img v-if="file.type.startsWith('image/')" :src="file.url" alt="Preview" />
        <div v-else class="file-preview">
          <i class="bi bi-file"></i>
          <span>{{ file.name }}</span>
        </div>
        <button @click="removePreview(index)" class="remove-preview">
          <i class="bi bi-x"></i>
        </button>
      </div>
    </div>

    <div v-if="audioPreview" class="audio-preview">
      <audio :src="audioPreview.url" controls></audio>
      <div class="audio-actions">
        <button @click="confirmAudio" class="btn btn-sm btn-success">
          <i class="bi bi-check"></i> Enviar
        </button>
        <button @click="cancelAudio" class="btn btn-sm btn-danger">
          <i class="bi bi-x"></i> Cancelar
        </button>
      </div>
    </div>

    <div v-if="requiresTemplate && !selectedTemplate" class="template-required-alert">
      <div class="alert alert-warning mb-2">
        <i class="bi bi-exclamation-triangle me-2"></i>
        <strong>Atenção:</strong> A janela de 24 horas expirou. Você precisa selecionar um template aprovado para enviar mensagens.
      </div>
      <div class="mb-2">
        <label class="form-label small">Selecione um template:</label>
        <select v-model="templateForm.template_id" class="form-select form-select-sm" @change="onTemplateChange">
          <option value="">Selecione um template</option>
          <option
            v-for="template in approvedTemplates"
            :key="template.id_whatsapp_template"
            :value="template.id_whatsapp_template"
          >
            {{ template.template_name }}
          </option>
        </select>
      </div>
      <div v-if="selectedTemplate && templateParameters.length > 0" class="mb-2">
        <label class="form-label small">Preencha as variáveis:</label>
        <div v-for="(param, index) in templateParameters" :key="index" class="mb-1">
          <input
            type="text"
            v-model="templateForm.template_parameters[index]"
            class="form-control form-control-sm"
            :placeholder="`Variável ${index + 1}`"
          />
        </div>
      </div>
    </div>

    <div class="input-toolbar">
      <button @click="toggleEmojiPicker" class="toolbar-btn" type="button">
        <i class="bi bi-emoji-smile"></i>
      </button>
      
      <button @click="triggerFileInput" class="toolbar-btn" type="button">
        <i class="bi bi-paperclip"></i>
      </button>
      
      <input
        ref="fileInput"
        type="file"
        multiple
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
        @change="handleFileSelect"
        style="display: none"
      />

      <button
        @click="toggleRecording"
        class="toolbar-btn record-btn"
        :class="{ 'recording': isRecording }"
        type="button"
        :disabled="loading"
      >
        <i class="bi" :class="isRecording ? 'bi-stop-circle' : 'bi-mic'"></i>
        <span v-if="isRecording" class="recording-waves">
          <span class="wave wave-1"></span>
          <span class="wave wave-2"></span>
          <span class="wave wave-3"></span>
        </span>
      </button>

      <div class="input-wrapper">
        <textarea
          ref="textarea"
          v-model="messageText"
          @keydown.enter.exact.prevent="handleEnter"
          @keydown.shift.enter.exact="handleShiftEnter"
          @keydown.escape="handleEscape"
          @keydown.up="handleArrowUp"
          @keydown.down="handleArrowDown"
          @input="handleInput"
          @keyup="handleKeyUp"
          placeholder="Digite uma mensagem... (use / para templates)"
          rows="1"
          class="message-textarea"
        ></textarea>
      </div>

      <button
        @click="sendMessage"
        :disabled="!canSend"
        class="send-btn"
        type="button"
      >
        <i class="bi bi-send"></i>
      </button>
    </div>

    <EmojiPicker
      :visible="showEmojiPicker"
      @select="insertEmoji"
    />

    <TemplateAutocomplete
      ref="templateAutocomplete"
      :show="showAutocomplete"
      :templates="templates"
      :search-query="templateSearchQuery"
      :position="autocompletePosition"
      @select="selectTemplate"
    />
  </div>
</template>

<script>
import AudioRecorder from "@/helpers/audioRecorder";
import Alert from "@/helpers/alert";
import whatsappHelper from "@/helpers/whatsapp";
import whatsappService from "@/service/whatsappService";
import TemplateAutocomplete from "./TemplateAutocomplete.vue";
import EmojiPicker from "./EmojiPicker.vue";

export default {
  name: 'MessageInput',
  components: {
    TemplateAutocomplete,
    EmojiPicker,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    replyingTo: {
      type: Object,
      default: null,
    },
    chat: {
      type: Object,
      default: null,
    },
    requiresTemplate: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['send', 'send-media', 'send-audio', 'cancel-reply'],
  data() {
    return {
      messageText: '',
      showEmojiPicker: false,
      previewFiles: [],
      audioPreview: null,
      audioRecorder: null,
      isRecording: false,
      templates: [],
      showAutocomplete: false,
      templateSearchQuery: '',
      autocompletePosition: { top: 0, left: 0 },
      lastSlashIndex: -1,
      templateForm: {
        template_id: null,
        template_parameters: [],
      },
    };
  },
  computed: {
    canSend() {
      if (this.requiresTemplate && !this.selectedTemplate) {
        return false;
      }
      return (this.messageText.trim() || this.previewFiles.length > 0 || this.audioPreview) && !this.loading;
    },
    place() {
      return this.$store.state.auth.place;
    },
    approvedTemplates() {
      return this.templates.filter(
        (t) => t.is_whatsapp_template && t.whatsapp_template_status === 'approved'
      );
    },
    selectedTemplate() {
      return this.templates.find((t) => t.id_whatsapp_template === this.templateForm.template_id);
    },
    templateParameters() {
      if (!this.selectedTemplate) return [];
      const content = this.selectedTemplate.template_content || '';
      const matches = content.match(/\{\{(\d+)\}\}/g);
      if (!matches) return [];
      return matches.map((match, index) => {
        const num = match.replace(/\{\{|\}\}/g, '');
        return {
          index: parseInt(num) - 1,
          label: `Variável ${num}`,
        };
      });
    },
  },
  watch: {
    messageText(newVal, oldVal) {
      this.$nextTick(() => {
        this.checkForSlash();
      });
    },
  },
  mounted() {
    this.audioRecorder = new AudioRecorder();
    this.audioRecorder.onRecordingStart = () => {
      this.isRecording = true;
    };
    this.audioRecorder.onRecordingStop = () => {
      this.isRecording = false;
    };
    this.audioRecorder.onRecordingComplete = (audioData) => {
      this.audioPreview = audioData;
      this.isRecording = false;
    };
    this.audioRecorder.onRecordingError = (error) => {
      console.error('Erro na gravação:', error);
      this.isRecording = false;
      Alert.toastError('Erro ao gravar áudio. Verifique as permissões do microfone.');
    };
    document.addEventListener('click', this.handleClickOutside);
    this.loadTemplates();
  },
  beforeUnmount() {
    if (this.audioRecorder) {
      this.audioRecorder.cleanup();
    }
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    insertEmoji(emoji) {
      this.messageText += emoji;
      this.showEmojiPicker = false;
      this.$nextTick(() => {
        this.adjustTextareaHeight();
      });
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      files.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.previewFiles.push({
            file,
            url: e.target.result,
            name: file.name,
            type: whatsappHelper.getMediaType(file.type), // Converte MIME type para tipo do WhatsApp
            mimeType: file.type, // Mantém o MIME type original
          });
        };
        reader.readAsDataURL(file);
      });
      event.target.value = '';
    },
    removePreview(index) {
      this.previewFiles.splice(index, 1);
    },
    async toggleRecording() {
      if (this.isRecording) {
        // Se está gravando, parar
        this.stopRecording();
      } else {
        // Se não está gravando, iniciar
        await this.startRecording();
      }
    },
    async startRecording() {
      try {
        // Se já está gravando, não fazer nada
        if (this.isRecording) {
          return;
        }

        // Se há um preview de áudio anterior, perguntar se quer substituir
        if (this.audioPreview) {
          const result = await Swal.fire({
            title: 'Gravar novo áudio?',
            text: 'A gravação anterior será descartada.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, gravar',
            cancelButtonText: 'Cancelar',
            customClass: {
              confirmButton: 'btn btn-primary',
              cancelButton: 'btn btn-secondary'
            }
          });

          if (!result.isConfirmed) {
            return;
          }

          this.audioPreview = null;
        }

        if (!this.audioRecorder.mediaRecorder) {
          const initialized = await this.audioRecorder.init();
          if (!initialized) {
            Alert.toastError('Erro ao acessar o microfone. Verifique as permissões.');
            return;
          }
        }
        
        const started = this.audioRecorder.startRecording();
        if (!started) {
          Alert.toastError('Não foi possível iniciar a gravação');
          this.isRecording = false;
        }
        // O estado isRecording será atualizado pelo callback onRecordingStart
      } catch (error) {
        console.error('Erro ao iniciar gravação:', error);
        Alert.toastError('Erro ao iniciar gravação de áudio');
        this.isRecording = false;
      }
    },
    stopRecording() {
      if (this.audioRecorder && (this.audioRecorder.isRecording || this.isRecording)) {
        this.audioRecorder.stopRecording();
      }
      // O estado isRecording será atualizado pelo callback onRecordingStop
    },
    confirmAudio() {
      this.$emit('send-audio', this.audioPreview);
      this.audioPreview = null;
    },
    cancelAudio() {
      this.audioPreview = null;
    },
    handleEnter() {
      if (this.showAutocomplete && this.$refs.templateAutocomplete) {
        this.$refs.templateAutocomplete.selectCurrent();
        return;
      }
      if (this.canSend) {
        this.sendMessage();
      }
    },
    handleShiftEnter() {
      this.messageText += '\n';
      this.$nextTick(() => {
        this.adjustTextareaHeight();
      });
    },
    handleInput() {
      this.adjustTextareaHeight();
      this.$nextTick(() => {
        this.checkForSlash();
      });
    },
    handleKeyUp(event) {
      if (event.key === '/' || event.key === 'Backspace' || event.key === 'Delete' || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        this.$nextTick(() => {
          this.checkForSlash();
        });
      }
    },
    checkForSlash() {
      const textarea = this.$refs.textarea;
      if (!textarea) return;
      
      const value = this.messageText;
      const cursorPos = textarea.selectionStart || value.length;
      const textBeforeCursor = value.substring(0, cursorPos);
      const lastSlash = textBeforeCursor.lastIndexOf('/');
      
      if (lastSlash !== -1) {
        const textAfterSlash = textBeforeCursor.substring(lastSlash + 1);
        if (!textAfterSlash.includes('\n') && !textAfterSlash.includes(' ')) {
          this.templateSearchQuery = textAfterSlash;
          this.lastSlashIndex = lastSlash;
          this.showAutocomplete = true;
          this.updateAutocompletePosition();
        } else {
          this.showAutocomplete = false;
        }
      } else {
        this.showAutocomplete = false;
        this.lastSlashIndex = -1;
      }
    },
    adjustTextareaHeight() {
      const textarea = this.$refs.textarea;
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
      }
    },
    handleClickOutside(event) {
      if (this.$refs.inputContainer && !this.$refs.inputContainer.contains(event.target)) {
        this.showEmojiPicker = false;
        this.showAutocomplete = false;
      }
    },
    async loadTemplates() {
      try {
        if (this.place && this.place.id_place) {
          const allTemplates = await whatsappService.getTemplates(this.place.id_place);
          this.templates = Array.isArray(allTemplates) ? allTemplates.filter(t => t.template_active) : [];
        }
      } catch (error) {
        console.error('Erro ao carregar templates:', error);
        this.templates = [];
      }
    },
    updateAutocompletePosition() {
      this.$nextTick(() => {
        const textarea = this.$refs.textarea;
        if (textarea && this.$refs.inputContainer) {
          const containerRect = this.$refs.inputContainer.getBoundingClientRect();
          this.autocompletePosition = {
            top: containerRect.top - 350,
            left: containerRect.left + 10,
          };
        }
      });
    },
    selectTemplate(template) {
      const textBeforeSlash = this.messageText.substring(0, this.lastSlashIndex);
      this.messageText = textBeforeSlash + template.template_content;
      this.showAutocomplete = false;
      this.lastSlashIndex = -1;
      this.$nextTick(() => {
        this.adjustTextareaHeight();
        this.$refs.textarea.focus();
      });
    },
    handleEscape() {
      if (this.showAutocomplete) {
        this.showAutocomplete = false;
      }
    },
    handleArrowUp(event) {
      if (this.showAutocomplete) {
        event.preventDefault();
        this.$refs.templateAutocomplete?.navigate('up');
      }
    },
    handleArrowDown(event) {
      if (this.showAutocomplete) {
        event.preventDefault();
        this.$refs.templateAutocomplete?.navigate('down');
      }
    },
    focus() {
      this.$nextTick(() => {
        const textarea = this.$refs.textarea;
        if (textarea) {
          textarea.focus();
        }
      });
    },
    async sendMessage() {
      if (!this.canSend) return;

      if (this.requiresTemplate && !this.selectedTemplate) {
        Alert.toastError('Selecione um template aprovado para enviar a mensagem');
        return;
      }

      if (this.audioPreview) {
        this.$emit('send-audio', this.audioPreview);
        this.audioPreview = null;
        return;
      }

      if (this.previewFiles.length > 0) {
        this.previewFiles.forEach(preview => {
          this.$emit('send-media', {
            file: preview.file,
            name: preview.name,
            type: preview.type
          });
        });
        this.previewFiles = [];
      }

      if (this.messageText.trim()) {
        const payload = {
          content: this.messageText.trim(),
        };

        if (this.requiresTemplate && this.selectedTemplate) {
          payload.template_id = this.selectedTemplate.id_whatsapp_template;
          payload.template_parameters = this.templateForm.template_parameters.filter((p) => p && p.trim());
        }

        this.$emit('send', payload);
        this.messageText = '';
        this.templateForm.template_id = null;
        this.templateForm.template_parameters = [];
        this.$nextTick(() => {
          this.adjustTextareaHeight();
        });
      }
    },
    onTemplateChange() {
      if (this.selectedTemplate) {
        const paramCount = this.templateParameters.length;
        this.templateForm.template_parameters = new Array(paramCount).fill('');
      } else {
        this.templateForm.template_parameters = [];
      }
    },
    onTemplateChange() {
      if (this.selectedTemplate) {
        const paramCount = this.templateParameters.length;
        this.templateForm.template_parameters = new Array(paramCount).fill('');
      } else {
        this.templateForm.template_parameters = [];
      }
    },
    clearInput() {
      this.messageText = '';
      this.previewFiles = [];
      this.audioPreview = null;
      this.showEmojiPicker = false;
      if (this.isRecording) {
        this.stopRecording();
      }
      this.$nextTick(() => {
        this.adjustTextareaHeight();
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = '';
        }
      });
    },
  },
}
</script>

<style scoped>
.message-input-container {
  background: #ffffff;
  padding: 12px 16px;
  border-top: 1px solid #e4e6ef;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.02);
  position: relative;
  flex-shrink: 0;
}

.reply-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f0f0f0;
  border-left: 3px solid #780f5a;
  border-radius: 4px;
  margin-bottom: 8px;
}

.reply-content {
  flex: 1;
  min-width: 0;
}

.reply-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #780f5a;
  margin-bottom: 4px;
}

.reply-header i {
  font-size: 0.85rem;
}

.reply-text {
  font-size: 0.85rem;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cancel-reply-btn {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;
  font-size: 1rem;
  line-height: 1;
}

.cancel-reply-btn:hover {
  color: #333;
}

.preview-files {
  display: flex;
  gap: 8px;
  padding: 8px;
  flex-wrap: wrap;
}

.preview-item {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
}

.preview-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.file-preview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #ddd;
}

.file-preview i {
  font-size: 1.5rem;
  color: #780f5a;
  margin-bottom: 4px;
}

.file-preview span {
  font-size: 0.7rem;
  text-align: center;
  padding: 0 4px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.remove-preview {
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.7rem;
}

.audio-preview {
  padding: 8px;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 8px;
}

.audio-preview audio {
  width: 100%;
  margin-bottom: 8px;
}

.audio-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.input-toolbar {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  background: #f5f8fa;
  border-radius: 24px;
  padding: 6px 12px;
  border: 1px solid #e4e6ef;
}

.toolbar-btn {
  background: none;
  border: none;
  color: #666;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background 0.2s;
}

.toolbar-btn:hover {
  background: #f0f2f5;
}

.record-btn {
  position: relative;
  overflow: visible;
}

.toolbar-btn.recording {
  color: #dc3545;
  background: rgba(220, 53, 69, 0.1);
}

.recording-waves {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: -1;
}

.wave {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  border: 2px solid #dc3545;
  border-radius: 50%;
  opacity: 0;
  animation: wave-pulse 1.5s ease-out infinite;
}

.wave-1 {
  animation-delay: 0s;
}

.wave-2 {
  animation-delay: 0.5s;
}

.wave-3 {
  animation-delay: 1s;
}

@keyframes wave-pulse {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.8;
  }
  100% {
    transform: translate(-50%, -50%) scale(2.5);
    opacity: 0;
  }
}

.input-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
}

.message-textarea {
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  padding: 8px 12px;
  font-size: 0.95rem;
  max-height: 120px;
  font-family: inherit;
}

.send-btn {
  background: linear-gradient(135deg, #780f5a 0%, #9d4779 100%);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(120, 15, 90, 0.2);
}

.send-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #5d0c47 0%, #780f5a 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(120, 15, 90, 0.3);
}

.send-btn:active:not(:disabled) {
  transform: translateY(0);
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

</style>

